// @ts-check

/* global mixpanel */
export const properties = {
  orgId: `Organization ID`,
  orgName: `Organization Name`,
  team: `Team`,
  userId: `User ID`,
  numUsers: `# of Users`,
  numServiceAccounts: `# of Service Accounts`,
  numProjects: `# of Projects`,
  type: `Type`,
  tab: `Tab`,
  screen: `Screen`,
  projectRole: `Project Role`,
  numOwners: `# of Owners`,
  numAdmins: `# of Admins`,
  numAnalysts: `# of Analysts`,
  numConsumers: `# of Consumers`,
  reportType: `Report Type`,
  customAlertId: `Custom Alert ID`,
  projectId: `Project ID`,
  projectName: `Project Name`,
  bookmarkId: `Bookmark ID`,

  // query timezone properties
  previousQueryTimeZone: `Previous Query Timezone`,
  newQueryTimeZone: `New Query Timezone`,

  // session settings properties
  errorMessage: `Error Message`,
  errorType: `Error Type`,
  eventName: `Event Name`,
  isProjectOwner: `Is Project Owner`,
  numEventsAdded: `# total events added alongside this event`,
  numEventsRemoved: `# total events removed alongside this event`,
  sessionTimeoutSeconds: `Session Timeout Seconds`,
  sessionTimeoutValue: `Session Timeout Value`,
  sessionTimeoutUnit: `Session Timeout Unit`,
  sessionDefinitionType: `Session Definition Type`,
  sessionCopyPropertiesCount: `Session Copy Properties Count`,

  // upsell
  featureLimit: `Feature Limit`,
  featureLimitTeamMembers: `Team Members`,

  // sensitive permission
  canView: `Can View`,
  numberOfUsersWithSensitiveAccess: `[Settings] # of Users with Sensitive Access`,
  numberOfTeamsWithSensitiveAccess: `[Settings] # of Teams with Sensitive Access`,

  // Usage Analytics
  usageAnalyticsContext: `Usage Analytics Context`,
  usageAnalyticsTab: `Usage Analytics Tab Selected`,
  chartName: `Chart Name`,
  chartFetchSuccess: `Chart Fetched Successfully`,
  chartFetchUniqueId: `Chart Fetch Unique Id`,
  chartFetchElapsedTime: `Chart Fetch Elapsed Time in Seconds`,
  successfullyLoadedChartNames: `Loaded Chart Names`,
  loadFailedChartNames: `Load Failed Chart Names`,
  selectedDateRange: `Selected Date Range`,

  // Public Dashboard
  enabledForProject: `Public Dashboard Enabled for Project`,
};

export const events = {
  // this event gets the appropriate [Org] [Project] [Account] prefix in code
  clickNavTab: `Click Navigation Tab`,

  viewOrg: `[Org] View Organization`,
  inviteUser: `[Org] Invite User`,
  inviteModalOpen: `[Org] Invite User Modal Open`,
  deleteProject: `[Org] Delete Project`,
  deleteUser: `[Org] Delete User`,
  createTeam: `[Org] Create Team`,
  createProject: `[Org] Create Project`,
  deleteTeam: `[Org] Delete Team`,
  downloadCsv: `[Org] Download CSV`,
  updateOrgRole: `[Org] Update Org Role`,
  addProjectToTeam: `[Org] Add Project(s) to Team`,
  addProjectToUser: `[Org] Add Project(s) to User`,
  deleteProjectFromTeam: `[Org] Delete Project(s) from Team`,
  resendInviteModal: `[Org] Open Resend Invite Modal`,
  generatedInviteUrls: `[Org] Generated Invite Urls`,
  orgServiceAccountCreated: `[Org] Service Account Created`,
  orgServiceAccountsRemoved: `[Org] Service Accounts Removed`,
  orgDeleteUserModal: `[Org] Open Delete User Modal`,

  // Upsell
  clickUpsell: `[Org] Invite Modal Click Upsell`,

  // Billing and Payment
  clickEditBilling: `[Org] Edit Billing Info`,
  clickRerunPayment: `[Org] Rerun Payment`,

  // User Settings
  userSetDarkMode: `[Account] Dark Mode Set`,
  viewAccountSettings: `[Account] View Account Settings`,
  unsubscribeAlerts: `[Account] Unsubscribe Alerts`,
  clickCustomAlertsTab: `[Account] Click Custom Alerts Tab`,
  openCustomAlertModal: `[Account] Open Custom Alert Editor Modal`,
  viewCustomAlertDetails: `[Account] View Custom Alert Details`,
  deleteCustomAlertFailed: `[Account] Failed to Delete Custom Alert`,
  customAlertDeleted: `[Account] Custom Alert Deleted`,

  // Session Settings events
  addExcludedEventForSessions: `[Project] Add Excluded Event for sessions`,
  editedSessionTimeout: `[Project] Edited Session Timeout`,
  inputErroneousSessionTimeout: `[Project] Error fired for editing session`,
  removeExcludedEventForSessions: `[Project] Remove Excluded Event for sessions`,
  sessionDefinitionTypeUpdated: `[Project] Session Definition Type Updated`,
  sessionCopyPropertiesUpdated: `[Project] Session Copy Properties Updated`,

  // Misc project settings events
  queryTimeZoneUpdated: `[Project] Query Timezone Updated`,
  viewProjectSettings: `[Project] View Project Settings`,
  viewWorkspaceSettings: `[Project] View Workspace Settings`,
  updateProjectTimePeriodSettings: `[Project] Update Time Period Settings`,
  editGroupKeyDisplayName: `[Project] Edit Group Key Display Name`,
  addGroupKey: `[Project] Add Group Key`,
  deleteGroupKey: `[Project] Delete Group Key`,

  // Project Users events
  projectResendInviteModal: `[Project] Open Resend Invite Modal`,
  projectServiceAccountCreated: `[Project] Service Account Created`,
  projectServiceAccountAdded: `[Project] Service Account Added`,
  projectServiceAccountsRemoved: `[Project] Service Accounts Removed`,
  projectDeleteUserModal: `[Project] Open Delete User Modal`,
  projectUsersDeleted: `[Project] Users Deleted`,
  projectUsersAdded: `[Project] Users Added`,
  projectUsersRemoved: `[Project] Users Removed`,
  projectUsersUpdated: `[Project] Users Removed`,
  projectGeneratedInviteUrls: `[Project] Generated Invite URL`,
  projectChangeSensitivePermission: `[Project] Users Changed Sensitive Permission`,
  projectLexiconClickLink: `[Project] Clicked Link to Lexicon`,

  // Usage Analytics events
  openOrgUsageAnalytics: `[Usage Analytics] Open Org Usage Analytics`,
  closeOrgUsageAnalytics: `[Usage Analytics] Close Org Usage Analytics`,
  openProjectUsageAnalytics: `[Usage Analytics] Open Project Usage Analytics`,
  closeProjectUsageAnalytics: `[Usage Analytics] Close Project Usage Analytics`,
  initiateChartFetch: `[Usage Analytics] Initiate Chart Data Fetch`,
  completeChartFetch: `[Usage Analytics] Chart Fetch Finished`,

  // Public Dashboard events
  publicDashboardUpdatedForProject: `[Public Dashboards] Updated for Project`,

  // Find Your Team
  clickBackToSignup: `[FYT] Clicked Back to Signup`,

  // Startup Credit Promo
  clickStartupPromoLink: `[Startup Promo] Click to Navigate to Price Builder`,
  viewedStartupCreditPromoTooltip: `[Startup Promo] Viewed Promo Tooltip`,
  startupCreditPromoFeatureEnabled: `[Startup Promo] Feature Enabled`,

  // Connect your account
  clickUpgrade: `[ConnectYourAccount] Click Upgrade in Nav`,
};

function mixpanelDefined() {
  return typeof mixpanel !== `undefined`;
}

export function track(eventName, properties = {}, callback = () => {}) {
  if (mixpanelDefined()) {
    mixpanel.track(`${eventName}`, properties, callback);
  }
}

export function register(properties) {
  if (mixpanelDefined()) {
    mixpanel.register(properties);
  }
}

export function setPeopleProperties(properties) {
  if (mixpanelDefined()) {
    mixpanel.people.set(properties);
  }
}

export function setGroupProperties(projectId, properties) {
  if (mixpanelDefined()) {
    mixpanel.get_group(`project_id`, projectId).set(properties);
  }
}
